import React, { useState } from "react";
import Chart from "react-apexcharts";
import {
  Box,
  Button,
  ButtonGroup,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { tokens } from "../../../../theme";
import { commonColors } from "../../../../theme/colors";

// Aggregation function to group data by days, months, or years.
const aggregateData = (rawData, viewMode) => {
  if (viewMode === "days") {
    // Convert date string to timestamp for each item.
    return rawData.map((item) => ({
      date: new Date(item.date).getTime(),
      value: item.value,
    }));
  } else if (viewMode === "months") {
    const groups = {};
    rawData.forEach((item) => {
      // Format as "MMM YYYY"
      const month = new Date(item.date).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      });
      if (!groups[month]) groups[month] = [];
      groups[month].push(item.value);
    });
    return Object.entries(groups).map(([date, values]) => ({
      date, // e.g. "Feb 2025"
      value: values.reduce((a, b) => a + b, 0) / values.length,
    }));
  } else if (viewMode === "years") {
    const groups = {};
    rawData.forEach((item) => {
      const year = new Date(item.date).getFullYear().toString();
      if (!groups[year]) groups[year] = [];
      groups[year].push(item.value);
    });
    return Object.entries(groups).map(([date, values]) => ({
      date, // e.g. "2025"
      value: values.reduce((a, b) => a + b, 0) / values.length,
    }));
  }
};

const TimeSeriesWeightChart = ({ rawData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [viewMode, setViewMode] = useState("days");

  // Aggregate data based on the selected view mode.
  const data = aggregateData(rawData, viewMode);

  // Build series so that each data point has an x and a y.
  // For "days" view, x is a timestamp (number); for other views, it's a string.
  const series = [
    {
      name: "Weight",
      data: data.map((item) => ({ x: item.date, y: item.value })),
    },
  ];

  // Configure x-axis:
  // If viewMode is "days", use datetime; otherwise, use category.
  const xaxisOptions =
    viewMode === "days"
      ? {
          type: "datetime",
          labels: {
            format: "dd MMM",
            style: {
              fontSize: isMobile ? "10px" : "12px",
              colors: ["#000"],
            },
          },
        }
      : {
          type: "category",
          labels: {
            style: {
              fontSize: isMobile ? "10px" : "12px",
              colors: ["#000"],
            },
          },
        };

  const options = {
    chart: {
      type: "line",
      zoom: { enabled: true },
      toolbar: { show: true },
      animations: { enabled: true },
      foreColor: "#000",
    },
    stroke: { curve: "smooth" },
    dataLabels: {
      enabled: true,
      style: { fontSize: isMobile ? "10px" : "12px", colors: ["#000"] },
    },
    xaxis: {
      ...xaxisOptions,
      title: {
        text: "Date",
        style: { fontSize: isMobile ? "10px" : "12px", color: "#000" },
      },
    },
    yaxis: {
      title: {
        text: "Weight (kg)",
        style: { fontSize: isMobile ? "10px" : "12px", color: "#000" },
      },
      labels: {
        style: { fontSize: isMobile ? "10px" : "12px", colors: ["#000"] },
      },
    },
    legend: {
      position: isMobile ? "bottom" : "right",
      horizontalAlign: isMobile ? "center" : "left",
      labels: { colors: ["#000"], fontSize: isMobile ? "10px" : "12px" },
    },
    tooltip: {
      theme: "light",
      style: { fontSize: isMobile ? "10px" : "12px", color: "#000" },
    },
    colors: [commonColors.protein],
  };

  return (
    <Box>
      <ButtonGroup size="small" variant="outlined" sx={{ mb: 2 }}>
        <Button onClick={() => setViewMode("days")}>Days</Button>
        <Button onClick={() => setViewMode("months")}>Months</Button>
        <Button onClick={() => setViewMode("years")}>Years</Button>
      </ButtonGroup>
      {/* Adding key based on viewMode to force reinitialization */}
      <Chart
        key={viewMode}
        options={options}
        series={series}
        type="line"
        height="400"
      />
    </Box>
  );
};

export default TimeSeriesWeightChart;
